import React, { useEffect, useState } from 'react';
import { Card, Input, Button, Space, Typography, Tooltip, Modal, Skeleton, Alert, Tag } from 'antd';
import { CopyOutlined, EyeOutlined, EyeInvisibleOutlined, ReloadOutlined, DeleteOutlined, KeyOutlined, SafetyOutlined } from '@ant-design/icons';
import { ApiServices } from '../../services/apiServices';
import { ApiConfig } from '../../services/ApiConfigs';
import { successMsg, errorMsg } from '../../utils/ShowMessage';

const { Text, Title } = Typography;

const StoreApiKeys = () => {
    const [apiKeys, setApiKeys] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showSecretKey, setShowSecretKey] = useState(false);
    const [copiedField, setCopiedField] = useState(null);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [isRegenerateModalVisible, setIsRegenerateModalVisible] = useState(false);

    useEffect(() => {
        fetchApiKeys();
    }, []);

    const fetchApiKeys = async () => {
        try {
            setLoading(true);
            const res = await ApiServices('get', ApiConfig.STORE_API_KEYS_MY_STORE);

            if (res.success && res.data) {
                setApiKeys(res.data);
            } else if (res.message && (res.message.includes('not found') || res.message.includes('Not found'))) {
                // API keys don't exist yet - this is normal, not an error
                setApiKeys(null);
            } else if (res.message) {
                // Other error messages
                console.warn('API Keys fetch warning:', res.message);
                setApiKeys(null);
            } else {
                // No keys found
                setApiKeys(null);
            }
        } catch (error) {
            console.error('Error fetching API keys:', error);
            // Don't show error if it's just that keys don't exist
            if (error.message && !error.message.includes('not found')) {
                errorMsg('Failed to fetch API keys');
            }
            setApiKeys(null);
        } finally {
            setLoading(false);
        }
    };

    const createApiKeys = async () => {
        try {
            setLoading(true);
            const res = await ApiServices('post', ApiConfig.STORE_API_KEYS);

            if (res.success && res.data) {
                setApiKeys(res.data);
                successMsg('API keys generated successfully!');
            } else if (res.message && res.message.includes('already exist')) {
                // Keys already exist, fetch them instead
                errorMsg('API keys already exist. Fetching existing keys...');
                await fetchApiKeys();
            } else {
                errorMsg(res.message || 'Failed to create API keys');
            }
        } catch (error) {
            console.error('Error creating API keys:', error);
            errorMsg(error.message || 'Failed to create API keys');
        } finally {
            setLoading(false);
        }
    };

    const regenerateApiKeys = async () => {
        try {
            setLoading(true);
            // Create new keys (this will regenerate if they exist)
            const res = await ApiServices('post', ApiConfig.STORE_API_KEYS);

            if (res.success && res.data) {
                setApiKeys(res.data);
                successMsg('API keys regenerated successfully!');
                setIsRegenerateModalVisible(false);
            } else {
                errorMsg(res.message || 'Failed to regenerate API keys');
            }
        } catch (error) {
            console.error('Error regenerating API keys:', error);
            errorMsg(error.message || 'Failed to regenerate API keys');
        } finally {
            setLoading(false);
        }
    };

    const deleteApiKeys = async () => {
        try {
            setLoading(true);
            const res = await ApiServices('delete', `${ApiConfig.STORE_API_KEYS}/${apiKeys.id}`);

            if (res.success) {
                setApiKeys(null);
                successMsg('API keys deleted successfully!');
                setIsDeleteModalVisible(false);
            } else {
                errorMsg(res.message || 'Failed to delete API keys');
            }
        } catch (error) {
            console.error('Error deleting API keys:', error);
            errorMsg(error.message || 'Failed to delete API keys');
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = async (text, fieldName) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(fieldName);
            successMsg(`${fieldName === 'apiKey' ? 'API Key' : 'Secret Key'} copied to clipboard!`);
            setTimeout(() => setCopiedField(null), 2000);
        } catch (err) {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopiedField(fieldName);
                successMsg(`${fieldName === 'apiKey' ? 'API Key' : 'Secret Key'} copied to clipboard!`);
                setTimeout(() => setCopiedField(null), 2000);
            } catch (fallbackErr) {
                errorMsg('Failed to copy to clipboard');
            }
            document.body.removeChild(textArea);
        }
    };

    return (
        <Card
            title={
                <Space>
                    <div style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <KeyOutlined style={{ fontSize: 16, color: '#ffffff' }} />
                    </div>
                    <span style={{ fontWeight: 600 }}>Store API Keys</span>
                </Space>
            }
            style={{ 
                height: '100%',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
            }}
            extra={
                apiKeys && !loading && (
                    <Space>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => setIsRegenerateModalVisible(true)}
                            danger
                        >
                            Regenerate
                        </Button>
                        <Button
                            icon={<DeleteOutlined />}
                            onClick={() => setIsDeleteModalVisible(true)}
                            danger
                        >
                            Delete
                        </Button>
                    </Space>
                )
            }
        >
                {loading && !apiKeys ? (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <div>
                            <Skeleton.Input active style={{ width: 80, marginBottom: 6 }} />
                            <Skeleton.Input active style={{ width: '100%', marginBottom: 4 }} />
                            <Skeleton.Input active style={{ width: 200 }} />
                        </div>
                        <div>
                            <Skeleton.Input active style={{ width: 80, marginBottom: 6 }} />
                            <Skeleton.Input active style={{ width: '100%', marginBottom: 4 }} />
                            <Skeleton.Input active style={{ width: 250 }} />
                        </div>
                    </Space>
                ) : !apiKeys ? (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '40px 20px',
                        background: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)',
                        borderRadius: 8,
                        border: '2px dashed #d9d9d9'
                    }}>
                        <div style={{
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 16px'
                        }}>
                            <KeyOutlined style={{ fontSize: 32, color: '#1890ff' }} />
                        </div>
                        <Title level={5} style={{ marginBottom: 8 }}>
                            No API Keys Generated
                        </Title>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 24, maxWidth: 300, margin: '0 auto 24px' }}>
                            Generate API keys to authenticate API requests for your store. These keys are required for secure API access.
                        </Text>
                        <Button
                            type="primary"
                            size="large"
                            icon={<KeyOutlined />}
                            onClick={createApiKeys}
                            loading={loading}
                            style={{
                                height: 40,
                                paddingLeft: 24,
                                paddingRight: 24
                            }}
                        >
                            Generate API Keys
                        </Button>
                    </div>
                ) : (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <div style={{
                            padding: 16,
                            background: '#fafafa',
                            borderRadius: 8,
                            border: '1px solid #f0f0f0'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                <Space>
                                    <Text strong style={{ fontSize: 14 }}>API Key</Text>
                                    <Tag color="blue" style={{ margin: 0 }}>Public</Tag>
                                </Space>
                            </div>
                            <div style={{ display: 'flex', marginBottom: 8, gap: 0, alignItems: 'stretch' }}>
                                <Input
                                    value={apiKeys.apiKey}
                                    readOnly
                                    size="large"
                                    style={{ 
                                        flex: 1, 
                                        fontSize: 13,
                                        fontFamily: 'monospace',
                                        background: '#ffffff'
                                    }}
                                />
                                <Tooltip title={copiedField === 'apiKey' ? 'Copied!' : 'Copy'}>
                                    <Button
                                        icon={<CopyOutlined />}
                                        onClick={() => copyToClipboard(apiKeys.apiKey, 'apiKey')}
                                        type={copiedField === 'apiKey' ? 'primary' : 'default'}
                                        size="large"
                                        style={{ 
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '4px 15px',
                                            borderRadius: '0 6px 6px 0',
                                            borderLeft: 'none'
                                        }}
                                    >
                                        {copiedField === 'apiKey' ? 'Copied' : 'Copy'}
                                    </Button>
                                </Tooltip>
                            </div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Use this key for public API requests. Safe to share in client-side code.
                            </Text>
                        </div>

                        <div style={{
                            padding: 16,
                            background: '#fff7e6',
                            borderRadius: 8,
                            border: '1px solid #ffe58f'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                <Space>
                                    <Text strong style={{ fontSize: 14 }}>Secret Key</Text>
                                    <Tag color="red" style={{ margin: 0 }}>Private</Tag>
                                </Space>
                            </div>
                            <div style={{ display: 'flex', marginBottom: 8, gap: 0, alignItems: 'stretch' }}>
                                <Input
                                    type={showSecretKey ? 'text' : 'password'}
                                    value={apiKeys.secretKey}
                                    readOnly
                                    size="large"
                                    style={{ 
                                        flex: 1, 
                                        fontSize: 13,
                                        fontFamily: 'monospace',
                                        background: '#ffffff'
                                    }}
                                />
                                <Tooltip title={showSecretKey ? 'Hide' : 'Show'}>
                                    <Button
                                        icon={showSecretKey ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                                        onClick={() => setShowSecretKey(!showSecretKey)}
                                        size="large"
                                        style={{ 
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '4px 12px',
                                            borderRadius: 0,
                                            borderLeft: 'none',
                                            borderRight: 'none'
                                        }}
                                    />
                                </Tooltip>
                                <Tooltip title={copiedField === 'secretKey' ? 'Copied!' : 'Copy'}>
                                    <Button
                                        icon={<CopyOutlined />}
                                        onClick={() => copyToClipboard(apiKeys.secretKey, 'secretKey')}
                                        type={copiedField === 'secretKey' ? 'primary' : 'default'}
                                        size="large"
                                        style={{ 
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '4px 15px',
                                            borderRadius: '0 6px 6px 0',
                                            borderLeft: 'none'
                                        }}
                                    >
                                        {copiedField === 'secretKey' ? 'Copied' : 'Copy'}
                                    </Button>
                                </Tooltip>
                            </div>
                            <Alert
                                message="Security Warning"
                                description="Keep this key secret and never expose it publicly. This key should only be used in server-side code."
                                type="warning"
                                showIcon
                                style={{ marginTop: 8 }}
                                icon={<SafetyOutlined />}
                            />
                        </div>

                        {apiKeys.Store && (
                            <div style={{
                                padding: 16,
                                backgroundColor: '#e6f7ff',
                                borderRadius: 8,
                                border: '1px solid #91d5ff'
                            }}>
                                <Text strong style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
                                    Associated Store Information
                                </Text>
                                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                    <div>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Store Name:</Text>
                                        <Text strong style={{ fontSize: 13, marginLeft: 8 }}>
                                            {apiKeys.Store.name}
                                        </Text>
                                    </div>
                                    <div>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Shop URL:</Text>
                                        <Text style={{ fontSize: 13, marginLeft: 8, fontFamily: 'monospace' }}>
                                            {apiKeys.Store.shopUrl}
                                        </Text>
                                    </div>
                                </Space>
                            </div>
                        )}
                    </Space>
                )}

                {/* Delete Confirmation Modal */}
                <Modal
                    title="Delete API Keys"
                    open={isDeleteModalVisible}
                    onOk={deleteApiKeys}
                    onCancel={() => setIsDeleteModalVisible(false)}
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    confirmLoading={loading}
                >
                    <p>Are you sure you want to delete these API keys? This action cannot be undone.</p>
                    <p style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                        ⚠️ All applications using these keys will stop working immediately.
                    </p>
                </Modal>

                {/* Regenerate Confirmation Modal */}
                <Modal
                    title="Regenerate API Keys"
                    open={isRegenerateModalVisible}
                    onOk={regenerateApiKeys}
                    onCancel={() => setIsRegenerateModalVisible(false)}
                    okText="Regenerate"
                    okButtonProps={{ danger: true }}
                    confirmLoading={loading}
                >
                    <p>Are you sure you want to regenerate these API keys?</p>
                    <p style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                        ⚠️ The current keys will be invalidated and all applications using them will stop working.
                    </p>
                    <p>You will need to update all applications with the new keys.</p>
                </Modal>
        </Card>
    );
};

export default StoreApiKeys;

